if (!customElements.get('lab-comparison')) {
  customElements.define(
    'lab-comparison',
    class LabComparison extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('[data-lab-range]');
        this.update = this.update.bind(this);
        this.range?.addEventListener('input', this.update);
        this.update();
      }

      update() {
        const value = this.range?.value || 50;
        this.style.setProperty('--position', `${value}%`);
      }

      disconnectedCallback() {
        this.range?.removeEventListener('input', this.update);
      }
    }
  );
}

if (!customElements.get('lumos-experience')) {
  customElements.define(
    'lumos-experience',
    class LumosExperience extends HTMLElement {
      connectedCallback() {
        this.gallery = this.querySelector('[data-lumos-gallery]');
        this.mainImage = this.querySelector('.lumos-gallery__main');
        this.galleryProof = this.querySelector('[data-lumos-gallery-proof]');
        this.galleryTechnology = this.querySelector('[data-lumos-gallery-technology]');
        this.galleryMessage = this.querySelector('[data-lumos-gallery-message]');
        this.galleryKicker = this.querySelector('[data-lumos-gallery-kicker]');
        this.galleryTitle = this.querySelector('[data-lumos-gallery-title]');
        this.galleryCopy = this.querySelector('[data-lumos-gallery-copy]');
        this.thumbs = [...this.querySelectorAll('[data-lumos-gallery-thumb]')];
        this.hero = this.querySelector('[data-lumos-hero]');
        this.finalOffer = this.querySelector('[data-lumos-final]');
        this.sticky = this.querySelector('[data-lumos-sticky]');
        this.immersiveSections = [...this.querySelectorAll('.lumos-cycle-section, .lumos-lab')];
        this.cycleVideo = this.querySelector('.lumos-cycle-film video');
        this.productForms = [...this.querySelectorAll('[data-hydros-product-form]')];
        this.colorChoices = [...this.querySelectorAll('[data-hydros-color-choice]')];
        this.selectImage = this.selectImage.bind(this);
        this.selectColor = this.selectColor.bind(this);
        this.updateSticky = this.updateSticky.bind(this);
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.selectImage));
        this.colorChoices.forEach((choice) => choice.addEventListener('change', this.selectColor));
        this.thumbs.find((thumb) => thumb.classList.contains('is-active'))?.click();
        window.addEventListener('scroll', this.updateSticky, { passive: true });
        window.addEventListener('resize', this.updateSticky, { passive: true });
        if (this.cycleVideo && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.cycleVideo.pause();
          this.cycleVideo.removeAttribute('autoplay');
        }
        this.updateSticky();
      }

      selectColor(event) {
        const choice = event.currentTarget;
        if (!choice.checked) return;
        const variantId = choice.dataset.variantId;
        const available = choice.dataset.variantAvailable === 'true';
        const colorName = choice.value;

        this.productForms.forEach((productForm) => {
          const select = productForm.querySelector('[data-hydros-variant-select]');
          const matchingChoice = [...productForm.querySelectorAll('[data-hydros-color-choice]')]
            .find((item) => item.dataset.variantId === variantId);
          const colorLabel = productForm.querySelector('[data-hydros-color-name]');
          const button = productForm.querySelector('[data-hydros-atc]');
          const buttonLabel = button?.querySelector('[data-hydros-atc-label]');
          if (select) select.value = variantId;
          if (matchingChoice) matchingChoice.checked = true;
          if (colorLabel) colorLabel.textContent = colorName;
          if (button) button.disabled = !available;
          if (buttonLabel) buttonLabel.textContent = available ? button.dataset.availableLabel : 'Indisponible';
        });

        this.querySelectorAll('.lumos-availability').forEach((node) => {
          node.classList.toggle('is-unavailable', !available);
          node.lastChild.textContent = available ? 'Disponible' : 'Indisponible';
        });
        const price = choice.dataset.variantPrice;
        this.querySelectorAll('.lumos-price .price-item--regular, .lumos-final__buy .price-item--regular').forEach((node) => {
          node.textContent = price;
        });

        const whiteSelected = /blanc|white/i.test(colorName);
        const targetColor = whiteSelected ? 'white' : 'black';
        const targetThumb = this.thumbs.find((thumb) => thumb.dataset.productColor === targetColor);
        targetThumb?.click();
      }

      selectImage(event) {
        const thumb = event.currentTarget;
        if (!this.mainImage) return;
        const proofSelected = thumb.dataset.galleryKind === 'proof';
        const technologySelected = thumb.dataset.galleryKind === 'technology';
        const specialViewSelected = proofSelected || technologySelected;
        this.mainImage.classList.toggle('is-hidden', specialViewSelected);
        this.galleryProof?.classList.toggle('is-hidden', !proofSelected);
        this.galleryProof?.setAttribute('aria-hidden', String(!proofSelected));
        this.galleryTechnology?.classList.toggle('is-hidden', !technologySelected);
        this.galleryTechnology?.setAttribute('aria-hidden', String(!technologySelected));
        if (!specialViewSelected) {
          this.mainImage.src = thumb.dataset.imageSrc;
          this.mainImage.srcset = thumb.dataset.imageSrcset || '';
          this.mainImage.alt = thumb.dataset.imageAlt || 'LUMØS';
          this.mainImage.style.objectPosition = thumb.dataset.imagePosition || 'center';
        }
        this.gallery?.style.setProperty('--gallery-bg', thumb.dataset.galleryBg || '#c2c8cf');
        this.mainImage.classList.toggle('is-cover', thumb.dataset.imageFit === 'cover');
        this.mainImage.classList.toggle('is-contain', thumb.dataset.imageFit !== 'cover');
        if (this.galleryMessage) {
          const hasMessage = !specialViewSelected && Boolean(thumb.dataset.captionTitle);
          this.galleryKicker.textContent = thumb.dataset.captionKicker || '';
          this.galleryTitle.textContent = thumb.dataset.captionTitle || '';
          this.galleryCopy.textContent = thumb.dataset.captionCopy || '';
          this.galleryMessage.dataset.theme = thumb.dataset.captionTheme || 'dark';
          this.galleryMessage.dataset.position = thumb.dataset.captionPosition || 'bottom-left';
          this.galleryMessage.classList.toggle('is-hidden', !hasMessage);
        }
        this.thumbs.forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
      }

      updateSticky() {
        if (!this.hero || !this.sticky) return;
        const heroPassed = this.hero.getBoundingClientRect().bottom < 80;
        const finalReached = this.finalOffer && this.finalOffer.getBoundingClientRect().top < window.innerHeight;
        const immersiveActive = this.immersiveSections.some((section) => {
          const rect = section.getBoundingClientRect();
          return rect.top < window.innerHeight * 0.82 && rect.bottom > window.innerHeight * 0.18;
        });
        this.sticky.classList.toggle('is-visible', heroPassed && !finalReached && !immersiveActive);
      }

      disconnectedCallback() {
        this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.selectImage));
        this.colorChoices.forEach((choice) => choice.removeEventListener('change', this.selectColor));
        window.removeEventListener('scroll', this.updateSticky);
        window.removeEventListener('resize', this.updateSticky);
      }
    }
  );
}
